import React, { useMemo, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import {
  FetchSandboxForEntitlementQuery,
  FetchSandboxForEntitlementQueryVariables,
  LinkedEntitlementTypes,
  LinkedProductTypes,
  RelationshipPathType,
  Maybe,
} from '@css-xp/graphtypes/src/generated-types-new';
import { DynamicResultTable, FormattedAddress } from '@css-xp/base';
import { NO_DETAILS_TO_SHOW } from '@atlassian/cssxp-utils-entitlement';
import { CloudProductDetailView, getBilledForColumnData } from '@css-xp/base/entitlement-utils';
import { fetchSandboxForEntitlement } from './queries/fetch-sandbox-for-entitlement';
import capitalize from 'lodash/capitalize';
import { useTransactionAccountContext } from '@css-xp/base/common-context';

export interface SandboxEntitlementTabProps {
  id?: string;
  currentEntitlementType: LinkedEntitlementTypes;
  destinationEntitlementType: Array<LinkedEntitlementTypes>;
  productType: Maybe<LinkedProductTypes> | undefined;
  findProducts?: boolean;
  findApps?: boolean;
  entitlementId: string;
  tableHeading?: string;
}

export const emptyStateProp = {
  header: '',
  description: NO_DETAILS_TO_SHOW,
  imageUrl: '',
  testId: 'no-results-sandbox-entitlement',
};

const SandboxEntitlementsTab = ({
  id,
  currentEntitlementType,
  destinationEntitlementType,
  productType,
  findApps,
  findProducts,
  entitlementId,
  tableHeading = 'Sandbox',
}: SandboxEntitlementTabProps) => {
  const { data, loading } = useQuery<FetchSandboxForEntitlementQuery, FetchSandboxForEntitlementQueryVariables>(
    fetchSandboxForEntitlement,
    {
      variables: {
        id: entitlementId,
        searchLinkedEntitlementParams: {
          entitlementId,
          containerId: id ?? null,
          currentEntitlementType,
          destinationEntitlementType,
          productType,
          findProducts,
          findApps,
        },
      },
      displayName: 'fetchSandboxEntitlements',
      notifyOnNetworkStatusChange: true,
    }
  );

  const sandboxEdges = useMemo(() => {
    if (currentEntitlementType === LinkedEntitlementTypes.PROD_ENTITLEMENT_PRODUCT) {
      // show only direct sandbox dependence entitlement for prod entitlement
      return data?.entitlementDetails?.sandboxEntitlements?.filter((sandboxEntitlementObj) => {
        const sandboxDependenceEntitlement = sandboxEntitlementObj?.relatesFromEntitlements?.find(
          (relationEntitlementObj) =>
            relationEntitlementObj?.relationshipType === RelationshipPathType.SANDBOX_DEPENDENCE &&
            relationEntitlementObj?.entitlementId === entitlementId
        );
        if (sandboxDependenceEntitlement) return true;
        return false;
      });
    }
    return data?.entitlementDetails.sandboxEntitlements;
  }, [data, currentEntitlementType, entitlementId]);

  const totalCount = sandboxEdges?.length || 0;
  const { data: transactionAccountData, getTransactionAccount } = useTransactionAccountContext();
  const transactionAccount = getTransactionAccount(transactionAccountData);

  const getSoldToAddress = useCallback(
    (invoiceGroupId: string | undefined | null) => {
      let currentInvoiceGroupDetails;
      if (!invoiceGroupId) {
        currentInvoiceGroupDetails = transactionAccount?.availableInvoiceGroups?.nodes[0];
      } else {
        currentInvoiceGroupDetails = transactionAccount?.availableInvoiceGroups?.nodes?.find(
          (invoiceGroupObj) => invoiceGroupObj?.id === invoiceGroupId
        );
      }
      if (currentInvoiceGroupDetails && currentInvoiceGroupDetails?.shipToParty) {
        return (
          <div className="line-clamp-sold-to-address">
            <FormattedAddress
              addressLine1={currentInvoiceGroupDetails?.shipToParty?.addressLine1}
              addressLine2={currentInvoiceGroupDetails?.shipToParty?.addressLine2}
              city={currentInvoiceGroupDetails?.shipToParty?.city}
              country={currentInvoiceGroupDetails?.shipToParty?.country}
              postcode={currentInvoiceGroupDetails?.shipToParty?.postcode}
              state={currentInvoiceGroupDetails?.shipToParty?.state}
              organizationName={currentInvoiceGroupDetails?.shipToParty?.name}
            />
          </div>
        );
      }
      return 'No address information';
    },
    [transactionAccount]
  );

  const getRows = () => {
    return (sandboxEdges ?? []).map((sandboxEdge, index) => {
      const sandboxNode = sandboxEdge as any;
      const siteName = sandboxNode?.cloudSiteHostname ?? 'N/A';

      return {
        key: sandboxNode?.entitlementNumber ?? index.toString(),
        cells: [
          {
            content: (
              <CloudProductDetailView
                productId={sandboxNode?.productId}
                entitlementNumber={sandboxNode?.entitlementNumber || ''}
                billingStatus={sandboxNode?.billingStatus}
                provisioningStatus={sandboxNode?.provisioningStatus}
                edition={sandboxNode?.edition}
                productName={sandboxNode?.productName}
                cloudSiteId={sandboxNode?.cloudSiteId}
                trial={sandboxNode?.trial ?? false}
                isApp={sandboxNode?.isApp}
                isSandbox={true}
              />
            ),
          },
          {
            content: (
              <div className="line-clamp site-name-container" title={siteName}>
                {siteName}
              </div>
            ),
          },
          {
            content: getSoldToAddress(sandboxNode?.billingGroupId),
          },
          {
            content: (
              <div>
                <div>{capitalize(getBilledForColumnData(sandboxNode?.productId, sandboxNode?.edition))}</div>
                <div>{sandboxNode?.activeUsers ?? '0'}</div>
              </div>
            ),
          },
        ],
      };
    });
  };

  const getHead = () => {
    const cells = [
      {
        content: 'Sandbox',
        width: 30,
      },
      {
        content: 'Site name',
        width: 20,
      },
      {
        content: 'Sold-to address',
        width: 20,
      },
      {
        content: 'Usage',
        width: 30,
      },
    ];
    return {
      cells,
    };
  };

  return (
    <div className="statuspage-tab" data-testid="statuspage-sandbox">
      <DynamicResultTable
        testId="sandbox-entitlements"
        headingText={tableHeading}
        head={getHead()}
        rows={getRows()}
        emptyStateProps={emptyStateProp}
        isLoading={loading}
        totalCount={totalCount}
        isPaginationEnabled={false}
      />
    </div>
  );
};

export default SandboxEntitlementsTab;
